import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { shallowEqual } from "react-redux";
import { usePackagesActions } from "@/store";
import { decideAutoResumeUploads } from "./decideAutoResume";

/**
 * Silent auto-resume for AUTO-paused uploads (bug 2026-08).
 *
 * Listens for network-recovery signals (online event + connection.change +
 * navigator.onLine transitions) and dispatches resumeUpload for any upload
 * currently in status=PAUSED with pauseReason='AUTO'. Per-uploadId cool-down
 * + attempt cap live in `decideAutoResumeUploads` (pure fn, unit tested).
 *
 * Placement: mounted alongside `CrossDeviceUploadsWatcher` in App.js so it's
 * alive whenever the contributor is signed in. Renders nothing.
 *
 * Cap on attempts (default 3) ensures a permanently-broken network doesn't
 * cause an endless auto-resume loop. Once the cap is hit the popup (fix 6)
 * takes over so the user learns something is genuinely wrong.
 */
export const AutoResumeWatcher = () => {
  const uploadingPackages = useSelector(
    (state) => state.packages.uploadingPackages,
    shallowEqual
  );
  const { resumeUpload } = usePackagesActions();

  // Per-uploadId dedupe state kept in refs so it survives re-renders without
  // triggering more of them. Cleared for uploadIds that have left the AUTO
  // paused set (they resumed successfully or got a different pause reason).
  const lastAttemptRef = useRef(new Map());
  const attemptsRef = useRef(new Map());

  const tryResumeNow = useCallback(() => {
    const ids = decideAutoResumeUploads(uploadingPackages, {
      now: Date.now(),
      lastAttemptByUploadId: lastAttemptRef.current,
      attemptsByUploadId: attemptsRef.current,
    });
    for (const uploadId of ids) {
      lastAttemptRef.current.set(uploadId, Date.now());
      attemptsRef.current.set(
        uploadId,
        (attemptsRef.current.get(uploadId) || 0) + 1
      );
      console.info("[UPLOAD-DIAG] auto-resume/attempt", {
        uploadId,
        attempt: attemptsRef.current.get(uploadId),
      });
      try {
        // silent: don't arm the "re-paused right after resume" toast for automatic retries.
        resumeUpload({ uploadId, silent: true });
      } catch (err) {
        console.warn("[UPLOAD-DIAG] auto-resume/error", {
          uploadId,
          message: err?.message,
        });
      }
    }
  }, [uploadingPackages, resumeUpload]);

  // Reset attempt counters for any uploadId no longer AUTO-paused (they
  // succeeded, the user paused it manually, or it went terminal). This is
  // what makes the "3 attempts and give up" cap self-heal — once the
  // upload gets past a single successful resume, the counter clears and
  // the next network dip gets a fresh 3-attempt budget.
  useEffect(() => {
    const currentAutoIds = new Set(
      (uploadingPackages || [])
        .filter((u) => u.status === "PAUSED" && u.pauseReason === "AUTO")
        .map((u) => u.uploadId)
    );
    for (const uploadId of Array.from(lastAttemptRef.current.keys())) {
      if (!currentAutoIds.has(uploadId)) {
        lastAttemptRef.current.delete(uploadId);
        attemptsRef.current.delete(uploadId);
      }
    }
  }, [uploadingPackages]);

  // Try immediately on mount so a page that opens with an AUTO-paused
  // upload already in Redux gets a chance to resume without waiting for a
  // network event.
  useEffect(() => {
    tryResumeNow();
    // Intentionally only on mount / dep-change of tryResumeNow itself;
    // the online/connection listeners below cover subsequent triggers.
  }, [tryResumeNow]);

  // Network-recovery signals. The pure fn's cool-down guards against
  // firing too frequently, so we don't need to debounce the listener.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => {
      console.info("[UPLOAD-DIAG] auto-resume/network-online-trigger");
      tryResumeNow();
    };
    window.addEventListener("online", handleOnline);

    const connection =
      (typeof navigator !== "undefined" &&
        (navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection)) ||
      null;
    if (connection && typeof connection.addEventListener === "function") {
      connection.addEventListener("change", handleOnline);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      if (connection && typeof connection.removeEventListener === "function") {
        connection.removeEventListener("change", handleOnline);
      }
    };
  }, [tryResumeNow]);

  return null;
};

export default AutoResumeWatcher;
